import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';

const VoiceAI = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [activeTab, setActiveTab] = useState('overview');
  const [vapiConfig, setVapiConfig] = useState({
    apiKey: '',
    isConfigured: false,
    assistantId: '',
    phoneNumber: ''
  });
  const [showConfigForm, setShowConfigForm] = useState(false);
  const [conversations, setConversations] = useState([
    {
      id: 1,
      caller: 'John Smith',
      phone: '+1 (555) 123-4567',
      duration: '5:42',
      status: 'completed',
      sentiment: 'positive',
      timestamp: '2025-11-26T10:30:00',
      summary: 'Customer inquiry about enterprise pricing. Interested in 100+ user licenses.',
      outcome: 'Lead qualified'
    },
    {
      id: 2,
      caller: 'Sarah Johnson',
      phone: '+1 (555) 987-6543',
      duration: '3:15',
      status: 'completed',
      sentiment: 'neutral',
      timestamp: '2025-11-26T09:15:00',
      summary: 'Support question regarding API integration. Provided documentation link.',
      outcome: 'Issue resolved'
    },
    {
      id: 3,
      caller: 'Mike Wilson',
      phone: '+1 (555) 456-7890',
      duration: '8:20',
      status: 'completed',
      sentiment: 'positive',
      timestamp: '2025-11-25T16:45:00',
      summary: 'Demo request for marketing automation features. Scheduled follow-up meeting.',
      outcome: 'Demo scheduled'
    }
  ]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [testingVoice, setTestingVoice] = useState(null);
  const [stats, setStats] = useState({
    totalCalls: 147,
    avgDuration: '4:32',
    positiveSentiment: 68,
    leadConversions: 23
  });

  const testVoice = (assistantName, voiceName) => {
    setTestingVoice(assistantName);
    
    // Use Web Speech API for voice testing
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(
        `Hello! I'm ${assistantName}. I'm here to help you with your needs. How can I assist you today?`
      );
      
      // Try to match the voice (this is a basic example)
      const voices = window.speechSynthesis.getVoices();
      const femaleVoice = voices.find(v => v.name.includes('Female') || v.name.includes('Samantha') || v.name.includes('Victoria'));
      const maleVoice = voices.find(v => v.name.includes('Male') || v.name.includes('Daniel') || v.name.includes('Alex'));
      
      if (voiceName.includes('Female') && femaleVoice) {
        utterance.voice = femaleVoice;
      } else if (voiceName.includes('Male') && maleVoice) {
        utterance.voice = maleVoice;
      }
      
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      utterance.onend = () => {
        setTestingVoice(null);
      };
      
      utterance.onerror = () => {
        setTestingVoice(null);
        alert('Voice synthesis failed. Please check your browser settings.');
      };
      
      window.speechSynthesis.speak(utterance);
    } else {
      setTestingVoice(null);
      alert('Voice synthesis is not supported in your browser. Please use Chrome, Safari, or Edge.');
    }
  };

  const stopVoiceTest = () => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setTestingVoice(null);
  };

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">
          Home
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">Voice AI</span>
      </nav>

      {/* Main Card */}
      <div className="card" style={{ background: isDark ? '#1f2937' : 'white', border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}` }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: isDark ? '#f9fafb' : 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              🎤 Voice AI Assistant
            </h2>
            <p style={{
              color: isDark ? '#9ca3af' : 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              AI-powered voice interactions and conversation analytics
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            borderBottom: `2px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
            gap: 'var(--space-1)'
          }}>
            {[
              { key: 'overview', label: '📊 Overview' },
              { key: 'conversations', label: '💬 Conversations' },
              { key: 'assistants', label: '🤖 AI Assistants' },
              { key: 'settings', label: '⚙️ VAPI Settings' }
            ].map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  padding: 'var(--space-3) var(--space-4)',
                  background: 'none',
                  border: 'none',
                  borderBottom: `3px solid ${activeTab === tab.key ? 'var(--primary-600)' : 'transparent'}`,
                  cursor: 'pointer',
                  fontWeight: '600',
                  color: activeTab === tab.key ? 'var(--primary-600)' : isDark ? '#9ca3af' : 'var(--gray-500)',
                  transition: 'all 0.3s',
                  whiteSpace: 'nowrap'
                }}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* VAPI Status Banner */}
            <div style={{
              padding: '1.5rem',
              border: `2px solid ${vapiConfig.isConfigured ? '#10b981' : '#f59e0b'}`,
              borderRadius: '8px',
              backgroundColor: vapiConfig.isConfigured 
                ? (isDark ? '#064e3b' : '#f0fdf4') 
                : (isDark ? '#78350f' : '#fffbeb'),
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontSize: '2rem' }}>
                  {vapiConfig.isConfigured ? '✅' : '⚙️'}
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem', marginBottom: '0.25rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                    {vapiConfig.isConfigured ? 'VAPI Connected' : 'VAPI Not Configured'}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: isDark ? '#d1d5db' : 'var(--gray-600)' }}>
                    {vapiConfig.isConfigured 
                      ? 'Voice AI is active and processing calls'
                      : 'Configure VAPI integration to enable voice AI features'}
                  </div>
                </div>
                {!vapiConfig.isConfigured && (
                  <button 
                    className="btn btn-primary"
                    onClick={() => setActiveTab('settings')}
                  >
                    Configure VAPI
                  </button>
                )}
              </div>
            </div>

            {/* Stats Grid */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}>
              {[
                { label: 'Total Calls', value: stats.totalCalls, icon: '📞', color: '#3b82f6' },
                { label: 'Avg Duration', value: stats.avgDuration, icon: '⏱️', color: '#8b5cf6' },
                { label: 'Positive Sentiment', value: `${stats.positiveSentiment}%`, icon: '😊', color: '#10b981' },
                { label: 'Lead Conversions', value: stats.leadConversions, icon: '🎯', color: '#f59e0b' }
              ].map((stat, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '1.5rem',
                    border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    borderLeft: `4px solid ${stat.color}`,
                    backgroundColor: isDark ? '#374151' : 'white'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.5rem' }}>{stat.icon}</span>
                    <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                      {stat.label}
                    </div>
                  </div>
                  <div style={{ fontSize: '2rem', fontWeight: 'bold', color: stat.color }}>
                    {stat.value}
                  </div>
                </div>
              ))}
            </div>

            {/* Recent Conversations */}
            <h4 style={{ marginBottom: '1rem', color: isDark ? '#f9fafb' : 'inherit' }}>Recent Conversations</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {conversations.slice(0, 3).map(conv => (
                <div
                  key={conv.id}
                  style={{
                    padding: '1.25rem',
                    border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    backgroundColor: isDark ? '#374151' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={() => {
                    setSelectedConversation(conv);
                    setActiveTab('conversations');
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? '#4b5563' : 'var(--gray-50)'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isDark ? '#374151' : 'white'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={{ fontSize: '1.5rem' }}>📞</span>
                      <div>
                        <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: isDark ? '#f9fafb' : 'inherit' }}>{conv.caller}</div>
                        <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                          {conv.phone} • {new Date(conv.timestamp).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                      <div style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        backgroundColor: conv.sentiment === 'positive' ? '#10b981' : conv.sentiment === 'negative' ? '#ef4444' : '#6b7280',
                        color: 'white'
                      }}>
                        {conv.sentiment === 'positive' ? '😊' : conv.sentiment === 'negative' ? '😞' : '😐'} {conv.sentiment}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                        {conv.duration}
                      </div>
                    </div>
                  </div>
                  <div style={{ fontSize: '0.875rem', color: isDark ? '#d1d5db' : 'var(--gray-700)', marginBottom: '0.5rem' }}>
                    {conv.summary}
                  </div>
                  <div style={{ fontSize: '0.875rem', color: isDark ? '#e5e7eb' : 'inherit' }}>
                    <strong>Outcome:</strong> <span style={{ color: 'var(--primary-600)' }}>{conv.outcome}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Conversations Tab */}
        {activeTab === 'conversations' && (
          <>
            {selectedConversation ? (
              /* Conversation Detail */
              <div>
                <button
                  onClick={() => setSelectedConversation(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--primary-600)',
                    cursor: 'pointer',
                    marginBottom: '1rem',
                    padding: '0.5rem 0',
                    fontSize: '0.875rem',
                    fontWeight: '600'
                  }}
                >
                  ← Back to Conversations
                </button>

                <div style={{
                  border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
                  borderRadius: '8px',
                  padding: '1.5rem',
                  marginBottom: '1.5rem',
                  backgroundColor: isDark ? '#374151' : 'white'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1.5rem' }}>
                    <div>
                      <h3 style={{ margin: '0 0 0.5rem 0', color: isDark ? '#f9fafb' : 'inherit' }}>{selectedConversation.caller}</h3>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                        {selectedConversation.phone} • {new Date(selectedConversation.timestamp).toLocaleString()}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>Duration</div>
                        <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: isDark ? '#f9fafb' : 'inherit' }}>{selectedConversation.duration}</div>
                      </div>
                      <div style={{
                        padding: '0.5rem 1rem',
                        borderRadius: '12px',
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        backgroundColor: selectedConversation.sentiment === 'positive' ? '#10b981' : selectedConversation.sentiment === 'negative' ? '#ef4444' : '#6b7280',
                        color: 'white'
                      }}>
                        {selectedConversation.sentiment === 'positive' ? '😊' : selectedConversation.sentiment === 'negative' ? '😞' : '😐'} {selectedConversation.sentiment}
                      </div>
                    </div>
                  </div>

                  <div style={{
                    padding: '1rem',
                    backgroundColor: isDark ? '#1f2937' : 'var(--gray-50)',
                    borderRadius: '8px',
                    marginBottom: '1.5rem'
                  }}>
                    <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>Summary</div>
                    <p style={{ margin: 0, lineHeight: '1.6', color: isDark ? '#d1d5db' : 'inherit' }}>{selectedConversation.summary}</p>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)', marginBottom: '0.25rem' }}>
                        Status
                      </div>
                      <div style={{ fontWeight: '600', textTransform: 'capitalize', color: isDark ? '#f9fafb' : 'inherit' }}>
                        {selectedConversation.status}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)', marginBottom: '0.25rem' }}>
                        Outcome
                      </div>
                      <div style={{ fontWeight: '600', color: 'var(--primary-600)' }}>
                        {selectedConversation.outcome}
                      </div>
                    </div>
                  </div>

                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '0.75rem', color: isDark ? '#f9fafb' : 'inherit' }}>🎯 Actions</div>
                    <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                      <button className="btn btn-primary" style={{ fontSize: '0.875rem' }}>
                        📧 Send Follow-up Email
                      </button>
                      <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                        📅 Schedule Call
                      </button>
                      <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                        📝 Create Lead
                      </button>
                      <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                        🎧 Play Recording
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              /* Conversations List */
              <div>
                <h3 style={{ marginTop: 0, marginBottom: '1rem', color: isDark ? '#f9fafb' : 'inherit' }}>All Conversations</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {conversations.map(conv => (
                    <div
                      key={conv.id}
                      style={{
                        padding: '1.25rem',
                        border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
                        borderRadius: '8px',
                        backgroundColor: isDark ? '#374151' : 'white',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onClick={() => setSelectedConversation(conv)}
                      onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? '#4b5563' : 'var(--gray-50)'}
                      onMouseLeave={(e) => e.currentTarget.style.backgroundColor = isDark ? '#374151' : 'white'}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <span style={{ fontSize: '1.5rem' }}>📞</span>
                          <div>
                            <div style={{ fontWeight: '600', marginBottom: '0.25rem', color: isDark ? '#f9fafb' : 'inherit' }}>{conv.caller}</div>
                            <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                              {conv.phone} • {new Date(conv.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                          <div style={{
                            padding: '0.25rem 0.75rem',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            backgroundColor: conv.sentiment === 'positive' ? '#10b981' : conv.sentiment === 'negative' ? '#ef4444' : '#6b7280',
                            color: 'white'
                          }}>
                            {conv.sentiment === 'positive' ? '😊' : conv.sentiment === 'negative' ? '😞' : '😐'} {conv.sentiment}
                          </div>
                          <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                            {conv.duration}
                          </div>
                        </div>
                      </div>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#d1d5db' : 'var(--gray-700)', marginBottom: '0.5rem' }}>
                        {conv.summary}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: isDark ? '#e5e7eb' : 'inherit' }}>
                        <strong>Outcome:</strong> <span style={{ color: 'var(--primary-600)' }}>{conv.outcome}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* AI Assistants Tab */}
        {activeTab === 'assistants' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, color: isDark ? '#f9fafb' : 'inherit' }}>Voice AI Assistants</h3>
              <button className="btn btn-primary">
                ➕ Create Assistant
              </button>
            </div>

            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                { 
                  name: 'Sales Assistant',
                  description: 'Handles inbound sales inquiries, qualifies leads, and books demos',
                  voice: 'Jennifer (Professional Female)',
                  language: 'English (US)',
                  status: 'active',
                  calls: 89
                },
                {
                  name: 'Support Assistant',
                  description: 'Provides customer support, troubleshooting, and FAQ answers',
                  voice: 'Michael (Professional Male)',
                  language: 'English (US)',
                  status: 'active',
                  calls: 156
                },
                {
                  name: 'Appointment Scheduler',
                  description: 'Books appointments, confirms meetings, and sends reminders',
                  voice: 'Sarah (Friendly Female)',
                  language: 'English (US)',
                  status: 'inactive',
                  calls: 23
                }
              ].map((assistant, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '1.5rem',
                    border: `1px solid ${assistant.status === 'active' ? 'var(--primary-600)' : isDark ? '#374151' : 'var(--gray-200)'}`,
                    borderRadius: '8px',
                    backgroundColor: assistant.status === 'active' 
                      ? (isDark ? '#1e3a5f' : '#eff6ff') 
                      : (isDark ? '#374151' : 'white')
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '1rem' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                        <h4 style={{ margin: 0, color: isDark ? '#f9fafb' : 'inherit' }}>{assistant.name}</h4>
                        <div style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          backgroundColor: assistant.status === 'active' ? '#10b981' : '#9ca3af',
                          color: 'white'
                        }}>
                          {assistant.status.toUpperCase()}
                        </div>
                      </div>
                      <p style={{ margin: '0 0 0.75rem 0', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                        {assistant.description}
                      </p>
                    </div>
                    <div style={{ fontSize: '2rem' }}>🤖</div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>Voice</div>
                      <div style={{ fontWeight: '600', color: isDark ? '#f9fafb' : 'inherit' }}>{assistant.voice}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>Language</div>
                      <div style={{ fontWeight: '600', color: isDark ? '#f9fafb' : 'inherit' }}>{assistant.language}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.75rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>Total Calls</div>
                      <div style={{ fontWeight: '600', color: 'var(--primary-600)' }}>{assistant.calls}</div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '0.75rem' }}>
                    <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                      ✏️ Edit
                    </button>
                    <button 
                      className="btn btn-secondary" 
                      style={{ fontSize: '0.875rem' }}
                      onClick={() => testingVoice === assistant.name ? stopVoiceTest() : testVoice(assistant.name, assistant.voice)}
                      disabled={testingVoice && testingVoice !== assistant.name}
                    >
                      {testingVoice === assistant.name ? '⏹️ Stop' : '🎧 Test Voice'}
                    </button>
                    <button className="btn btn-secondary" style={{ fontSize: '0.875rem' }}>
                      {assistant.status === 'active' ? '⏸️ Deactivate' : '▶️ Activate'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div>
            <h3 style={{ marginTop: 0, marginBottom: '1.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>VAPI Integration Settings</h3>

            <div style={{
              padding: '1.5rem',
              border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
              borderRadius: '8px',
              backgroundColor: isDark ? '#374151' : 'white',
              marginBottom: '1.5rem'
            }}>
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                  VAPI API Key
                </label>
                <input
                  type="password"
                  className="form-control"
                  placeholder="Enter your VAPI API key"
                  defaultValue={vapiConfig.apiKey}
                  style={{ width: '100%', marginBottom: '0.25rem', backgroundColor: isDark ? '#1f2937' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                />
                <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                  Get your API key from <a href="https://vapi.ai" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-600)' }}>vapi.ai dashboard</a>
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                  Assistant ID (Optional)
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Default assistant ID"
                  defaultValue={vapiConfig.assistantId}
                  style={{ width: '100%', marginBottom: '0.25rem', backgroundColor: isDark ? '#1f2937' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                />
                <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                  Optional: Specify a default assistant for incoming calls
                </div>
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                  Phone Number
                </label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="+1 (555) 000-0000"
                  defaultValue={vapiConfig.phoneNumber}
                  style={{ width: '100%', marginBottom: '0.25rem', backgroundColor: isDark ? '#1f2937' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                />
                <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)' }}>
                  Your VAPI phone number for inbound/outbound calls
                </div>
              </div>

              <button 
                className="btn btn-primary"
                onClick={() => {
                  setVapiConfig({...vapiConfig, isConfigured: true});
                  alert('✓ VAPI integration configured successfully!');
                }}
              >
                Save Configuration
              </button>
            </div>

            {/* Webhook Configuration */}
            <div style={{
              padding: '1.5rem',
              border: `1px solid ${isDark ? '#374151' : 'var(--gray-200)'}`,
              borderRadius: '8px',
              backgroundColor: isDark ? '#1f2937' : 'var(--gray-50)'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '1rem', color: isDark ? '#f9fafb' : 'inherit' }}>Webhook Configuration</h4>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', fontWeight: '600', marginBottom: '0.5rem', color: isDark ? '#f9fafb' : 'inherit' }}>
                  Webhook URL
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={`${window.location.origin}/api/webhooks/vapi`}
                  readOnly
                  style={{ width: '100%', backgroundColor: isDark ? '#374151' : 'white', color: isDark ? '#f9fafb' : 'inherit', border: `1px solid ${isDark ? '#4b5563' : 'var(--gray-300)'}` }}
                />
                <div style={{ fontSize: '0.875rem', color: isDark ? '#9ca3af' : 'var(--gray-600)', marginTop: '0.5rem' }}>
                  Configure this URL in your VAPI dashboard to receive call events
                </div>
              </div>
              <button 
                className="btn btn-secondary"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/vapi`);
                  alert('✓ Webhook URL copied to clipboard!');
                }}
              >
                📋 Copy to Clipboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VoiceAI;
